/**
 * SettingsLayout component with grouped secondary sidebar navigation.
 * Each item shows an icon, title, and description.
 */

import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Bot,
  Building2,
  ChevronRight,
  Database,
  KeyRound,
  Link2,
  Lock,
  Palette,
  Stethoscope,
  User,
  Users,
  Zap,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

interface SettingsLayoutProps {
  children: ReactNode
}

interface NavigationItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  description: string
}

interface NavigationGroup {
  label: string
  items: NavigationItem[]
}

/**
 * Builds navigation groups with app-scoped settings paths
 */
function getNavigationGroups(appId: string): NavigationGroup[] {
  const base = `/app/${appId}/settings`
  return [
    {
      label: 'Account',
      items: [
        {
          title: 'Profile',
          href: `${base}/profile-account`,
          icon: User,
          description: 'Your name, photo, and email',
        },
        {
          title: 'Preferences',
          href: `${base}/preferences`,
          icon: Palette,
          description: 'Theme, language, notifications',
        },
        {
          title: 'Security',
          href: `${base}/security`,
          icon: Lock,
          description: 'Passwords, SSO, and SSL/TLS',
        },
      ],
    },
    {
      label: 'Workspace',
      items: [
        {
          title: 'General',
          href: `${base}/workspace-settings`,
          icon: Building2,
          description: 'Workspace name and switcher',
        },
        {
          title: 'AI Configuration',
          href: `${base}/ai-configuration`,
          icon: Bot,
          description: 'Models and agent defaults',
        },
        {
          title: 'Team Members',
          href: `${base}/team-members`,
          icon: Users,
          description: 'Members, roles, invitations',
        },
        {
          title: 'Usage & Billing',
          href: `${base}/credits`,
          icon: Zap,
          description: 'Credits and billing',
        },
      ],
    },
    {
      label: 'Integrations',
      items: [
        {
          title: 'Connected Apps',
          href: `${base}/integrations`,
          icon: Link2,
          description: 'Third-party app connections',
        },
      ],
    },
    {
      label: 'Developer',
      items: [
        {
          title: 'API Keys',
          href: `${base}/api-keys`,
          icon: KeyRound,
          description: 'Manage API keys and tokens',
        },
        {
          title: 'Datasets',
          href: `${base}/datasets`,
          icon: Database,
          description: 'Structured data storage',
        },
        {
          title: 'Diagnostics',
          href: `${base}/diagnostics`,
          icon: Stethoscope,
          description: 'System health and logs',
        },
      ],
    },
  ]
}

/**
 * Checks if a navigation item is currently active
 */
function isActiveRoute(href: string, pathname: string): boolean {
  return pathname === href || pathname.startsWith(href + '/')
}

/**
 * Navigation item component with icon, title, description, and active indicator
 */
function NavigationLink({
  item,
  isActive,
}: {
  item: NavigationItem
  isActive: boolean
}) {
  const Icon = item.icon

  return (
    <Link
      to={item.href}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
        isActive
          ? 'bg-primary/5 border border-primary/20'
          : 'hover:bg-muted border border-transparent'
      )}
    >
      <div
        className={cn(
          'w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors',
          isActive ? 'bg-primary/10 dark:bg-primary/20' : 'bg-muted'
        )}
      >
        <Icon
          className={cn(
            'w-3.5 h-3.5',
            isActive ? 'text-primary' : 'text-muted-foreground'
          )}
        />
      </div>

      <div className="flex-1 min-w-0">
        <span
          className={cn(
            'type-control font-semibold',
            isActive ? 'text-primary' : 'text-foreground'
          )}
        >
          {item.title}
        </span>
        <p
          className={cn(
            'type-body-sm mt-1 line-clamp-1',
            isActive ? 'text-primary/80' : 'text-muted-foreground'
          )}
        >
          {item.description}
        </p>
      </div>

      {isActive && <ChevronRight className="w-4 h-4 text-primary shrink-0" />}
    </Link>
  )
}

/**
 * Settings sidebar with grouped navigation
 */
function SettingsSidebar() {
  const location = useLocation()
  const { appId } = useParams<{ appId: string }>()
  const navigationGroups = getNavigationGroups(appId || '')

  return (
    <div className="w-[280px] h-full border-r bg-background shrink-0 hidden lg:block">
      <ScrollArea className="h-full">
        <div className="px-3 py-4 space-y-5">
          {navigationGroups.map((group) => (
            <div key={group.label}>
              <p className="type-label px-3 mb-1.5 text-muted-foreground">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavigationLink
                    key={item.href}
                    item={item}
                    isActive={isActiveRoute(item.href, location.pathname)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

/**
 * SettingsLayout — settings sidebar on the left, content on the right
 */
export function SettingsLayout({ children }: SettingsLayoutProps) {
  return (
    <div className="h-full flex bg-background">
      <SettingsSidebar />
      <main className="flex-1 overflow-auto min-w-0">
        <div className="mx-auto w-full max-w-5xl px-6 py-6 lg:px-8 lg:py-8">
          {children}
        </div>
      </main>
    </div>
  )
}

SettingsLayout.displayName = 'SettingsLayout'
